import type { SpellcheckIssue } from '@/api/spellcheck';
import type { Node as ProseMirrorNode } from '@tiptap/pm/model';

export const SPELLCHECK_TYPO_CLASS = 'spellcheck-typo';

const BLOCK_TAGS = new Set([
    'ADDRESS',
    'ARTICLE',
    'ASIDE',
    'BLOCKQUOTE',
    'DD',
    'DIV',
    'DL',
    'DT',
    'FIGCAPTION',
    'FIGURE',
    'FOOTER',
    'H1',
    'H2',
    'H3',
    'H4',
    'H5',
    'H6',
    'HEADER',
    'HR',
    'LI',
    'MAIN',
    'NAV',
    'OL',
    'P',
    'PRE',
    'SECTION',
    'TABLE',
    'TBODY',
    'TD',
    'TFOOT',
    'TH',
    'THEAD',
    'TR',
    'UL',
]);

type HtmlTextRange = {
    start: number;
    end: number;
    node: Text;
};

type ProseMirrorTextRange = {
    start: number;
    end: number;
    from: number;
    to: number;
};

type ProseMirrorIssueRange = {
    from: number;
    to: number;
    issueIndex: number;
};

const createHtmlContainer = (html: string) => {
    const container = document.createElement('div');
    container.innerHTML = html;
    return container;
};

const collectHtmlTextRanges = (root: Node) => {
    const ranges: HtmlTextRange[] = [];
    const textParts: string[] = [];
    let offset = 0;

    const appendText = (value: string) => {
        if (!value) return;

        textParts.push(value);
        offset += value.length;
    };

    const visit = (node: Node) => {
        if (node.nodeType === Node.TEXT_NODE) {
            const value = node.textContent || '';

            if (value) {
                ranges.push({
                    start: offset,
                    end: offset + value.length,
                    node: node as Text,
                });
                appendText(value);
            }

            return;
        }

        if (node.nodeType !== Node.ELEMENT_NODE && node.nodeType !== Node.DOCUMENT_FRAGMENT_NODE) {
            return;
        }

        const element = node as HTMLElement;
        const tagName = element.tagName;

        if (tagName === 'SCRIPT' || tagName === 'STYLE') return;

        Array.from(node.childNodes).forEach(visit);

        if (BLOCK_TAGS.has(tagName)) {
            appendText('\n');
        }
    };

    visit(root);

    return {
        ranges,
        text: textParts.join(''),
    };
};

export const getHtmlTextForSpellcheck = (html: string) => {
    if (typeof document === 'undefined') return '';

    return collectHtmlTextRanges(createHtmlContainer(html)).text;
};

export const decorateHtmlWithSpellcheckIssues = (html: string, issues: SpellcheckIssue[]) => {
    if (typeof document === 'undefined' || issues.length === 0) return html;

    const container = createHtmlContainer(html);
    const { ranges } = collectHtmlTextRanges(container);

    for (const range of ranges) {
        const overlappingIssues = issues
            .map((issue, issueIndex) => ({ issue, issueIndex }))
            .filter(({ issue }) => issue.start < range.end && issue.end > range.start)
            .sort((a, b) => a.issue.start - b.issue.start);

        if (overlappingIssues.length === 0) continue;

        const value = range.node.textContent || '';
        const fragment = document.createDocumentFragment();
        let cursor = 0;

        for (const { issue, issueIndex } of overlappingIssues) {
            const localStart = Math.max(issue.start, range.start) - range.start;
            const localEnd = Math.min(issue.end, range.end) - range.start;

            if (localStart < cursor || localEnd <= localStart) continue;

            if (localStart > cursor) {
                fragment.append(value.slice(cursor, localStart));
            }

            const span = document.createElement('span');
            span.className = SPELLCHECK_TYPO_CLASS;
            span.dataset.spellcheckIssueIndex = String(issueIndex);
            span.textContent = value.slice(localStart, localEnd);
            fragment.append(span);
            cursor = localEnd;
        }

        if (cursor < value.length) {
            fragment.append(value.slice(cursor));
        }

        range.node.parentNode?.replaceChild(fragment, range.node);
    }

    return container.innerHTML;
};

const findHtmlPosition = (ranges: HtmlTextRange[], offset: number) => {
    for (const range of ranges) {
        if (offset >= range.start && offset <= range.end) {
            return {
                node: range.node,
                offset: offset - range.start,
            };
        }
    }

    return null;
};

export const replaceHtmlSpellcheckRange = (
    html: string,
    issue: SpellcheckIssue,
    replacement: string,
) => {
    if (typeof document === 'undefined') return html;

    const container = createHtmlContainer(html);
    const { ranges } = collectHtmlTextRanges(container);
    const start = findHtmlPosition(ranges, issue.start);
    const end = findHtmlPosition(ranges, issue.end);

    if (!start || !end) return html;

    const range = document.createRange();
    range.setStart(start.node, start.offset);
    range.setEnd(end.node, end.offset);
    range.deleteContents();
    range.insertNode(document.createTextNode(replacement));

    return container.innerHTML;
};

const collectProseMirrorTextRanges = (doc: ProseMirrorNode) => {
    const ranges: ProseMirrorTextRange[] = [];
    const textParts: string[] = [];
    let offset = 0;

    const appendText = (value: string) => {
        if (!value) return;

        textParts.push(value);
        offset += value.length;
    };

    const visit = (node: ProseMirrorNode, position: number) => {
        if (node.isText) {
            const value = node.text || '';

            if (value) {
                ranges.push({
                    start: offset,
                    end: offset + value.length,
                    from: position,
                    to: position + value.length,
                });
                appendText(value);
            }

            return;
        }

        node.forEach((child, childOffset) => {
            const childPosition =
                node.type.name === 'doc' ? childOffset : position + 1 + childOffset;
            visit(child, childPosition);
        });

        if (node.isBlock && node.type.name !== 'doc') {
            appendText('\n');
        }
    };

    visit(doc, 0);

    return {
        ranges,
        text: textParts.join(''),
    };
};

const findProseMirrorPosition = (ranges: ProseMirrorTextRange[], offset: number) => {
    for (const range of ranges) {
        if (offset >= range.start && offset <= range.end) {
            return range.from + (offset - range.start);
        }
    }

    return null;
};

export const getProseMirrorTextForSpellcheck = (doc: ProseMirrorNode) =>
    collectProseMirrorTextRanges(doc).text;

export const findProseMirrorRangeForIssue = (doc: ProseMirrorNode, issue: SpellcheckIssue) => {
    const { ranges } = collectProseMirrorTextRanges(doc);
    const from = findProseMirrorPosition(ranges, issue.start);
    const to = findProseMirrorPosition(ranges, issue.end);

    if (from === null || to === null || to <= from) return null;

    return { from, to };
};

export const getProseMirrorIssueRanges = (
    doc: ProseMirrorNode,
    issues: SpellcheckIssue[],
): ProseMirrorIssueRange[] => {
    const { ranges } = collectProseMirrorTextRanges(doc);

    return issues.flatMap((issue, issueIndex) => {
        const from = findProseMirrorPosition(ranges, issue.start);
        const to = findProseMirrorPosition(ranges, issue.end);

        if (from === null || to === null || to <= from) return [];

        return [{ from, to, issueIndex }];
    });
};
